import { useCallback, useMemo, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { AddRounded, FilterAltRounded } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import { useNavigate } from "react-router-dom";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { createSalesOrderPath, selectedSalesOrderPath } from "components/PimDashboard/data";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { soUrlKey } from "../CreateSalesOrder/data";
import { SoGinesysSyncBadge, SoStatusBadge, SoYesNoBadge } from "../CreateSalesOrder/soUi";
import FilterModal from "./FilterModal/FilterModal";
import { getSoFilterColumns, listSalesOrdersApi, lsSoFilterValStr, soListTableColumns } from "./data";

const AllSalesOrders = () => {
	const navigate = useNavigate();
	const { setLoadingOpen, snackBar, setSnackBarOpen, setSnackBar } = GlobalState();
	const [data, setData] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [filterModal, setFilterModal] = useState(false);
	const [filterColumns, setFilterColumns] = useState(() => {
		if (!localStorage.getItem(lsSoFilterValStr)) return [];
		const localStorageObj = JSON.parse(localStorage.getItem(lsSoFilterValStr));
		if (typeof localStorageObj !== "object" || !localStorageObj) return [];
		return getSoFilterColumns().filter((row) => localStorageObj[row.key] !== undefined);
	});
	const [filterColumnVal, setFilterColumnVal] = useState(() => {
		if (localStorage.getItem(lsSoFilterValStr)) {
			return JSON.parse(localStorage.getItem(lsSoFilterValStr));
		}
		return {};
	});

	const filterColumnOptions = useMemo(() => getSoFilterColumns(), []);

	const tableColumns = useMemo(
		() =>
			soListTableColumns.map((col) => {
				if (col.key === "status") {
					return {
						...col,
						renderCell: ({ row }) => (
							<Box width="100%" display="flex" justifyContent="center">
								<SoStatusBadge status={row.status} />
							</Box>
						),
					};
				}
				if (col.key === "order_date") {
					return {
						...col,
						renderCell: ({ row }) => (row.order_date ? String(row.order_date).slice(0, 10) : "—"),
					};
				}
				if (col.key === "is_authorized") {
					return {
						...col,
						renderCell: ({ row }) => (
							<Box width="100%" display="flex" justifyContent="center">
								<SoYesNoBadge value={row.is_authorized} />
							</Box>
						),
					};
				}
				if (col.key === "ginesys_sync") {
					return {
						...col,
						renderCell: ({ row }) => (
							<Box width="100%" display="flex" justifyContent="center">
								<SoGinesysSyncBadge value={row.ginesys_sync} />
							</Box>
						),
					};
				}
				return col;
			}),
		[]
	);

	const fetchData = useCallback(
		async (limit, offset, filter) => {
			const isFirstLoad = data.length === 0;
			if (isFirstLoad) setLoadingOpen(true);
			try {
				const result = await listSalesOrdersApi({ limit, offset, filter });
				return {
					rows: result.rows || [],
					total_rows_count: result.total_rows_count ?? 0,
				};
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					severity: "error",
					message: parseErrorMessage(error),
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				return { rows: [], total_rows_count: 0 };
			} finally {
				if (isFirstLoad) setLoadingOpen(false);
			}
		},
		[data.length, setLoadingOpen, setSnackBar, setSnackBarOpen, snackBar]
	);

	const handleRowClick = ({ row }) => {
		if (!row?.id) return;
		navigate(`/${selectedSalesOrderPath}?${soUrlKey}=${row.id}`);
	};

	return (
		<Box
			sx={{
				height: "calc(100vh - 135px)",
				p: 1,
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				gap: 1,
			}}>
			<Box display="flex" columnGap={1} pt={0.5} flexWrap="wrap" alignItems="center" rowGap="5px">
				<Button
					variant="contained"
					size="small"
					startIcon={<FilterAltRounded />}
					onClick={() => setFilterModal(true)}
					sx={{ textTransform: "capitalize" }}>
					Filter
				</Button>
				:
				{!filterColumns?.length && (
					<Typography variant="body1" fontSize={16} color={grey[700]}>
						None
					</Typography>
				)}
				{filterColumns?.map((row) => (
					<Chip
						key={row.key}
						label={`${row.name} : ${
							Array.isArray(filterColumnVal[row.key])
								? filterColumnVal[row.key]?.join(", ")
								: filterColumnVal?.[row.key]
						}`}
						size="small"
					/>
				))}
			</Box>

			<Box sx={{ flex: 1, minHeight: 0 }}>
				<TanstackDatagrid
					columns={tableColumns}
					pageData={data}
					setPageData={setData}
					rowCount={rowCount}
					setRowCount={setRowCount}
					filterObj={filterColumnVal}
					setFilterObj={setFilterColumnVal}
					disableColumnFilter
					disableColumnSort
					disablePagination
					isLoading={false}
					emptyMessage="No sales orders found"
					handleRowClick={handleRowClick}
					getServerSideData={fetchData}
					getRowId={(row) => row.id}>
					<Box sx={{ display: "flex", alignItems: "center", p: 1, justifyContent: "space-between" }}>
						<Typography variant="body2" color="text.secondary">
							{rowCount ? `${rowCount} sales order${rowCount === 1 ? "" : "s"}` : "Sales orders"}
						</Typography>
						<Button
							variant="contained"
							size="small"
							startIcon={<AddRounded />}
							onClick={() => navigate(`/${createSalesOrderPath}`)}
							sx={{ textTransform: "capitalize" }}>
							Create
						</Button>
					</Box>
				</TanstackDatagrid>
			</Box>

			{filterModal && (
				<FilterModal
					modal={filterModal}
					handleCloseModal={() => setFilterModal(false)}
					filterColumns={filterColumns}
					setFilterColumns={setFilterColumns}
					filterColumnVal={filterColumnVal}
					setFilterColumnVal={setFilterColumnVal}
					localStorageStr={lsSoFilterValStr}
					allColumns={filterColumnOptions}
				/>
			)}
		</Box>
	);
};

export default AllSalesOrders;
